import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAppContext } from './AppContext';

const WebSocketContext = createContext();
const WS_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { currentUser, activeSession, uncertainties, setUncertainties, votes, setVotes } = useAppContext();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    // Connect to WebSocket server
    socketRef.current = io(WS_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('WebSocket connected');
      setIsConnected(true);
    });

    socketRef.current.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    });

    socketRef.current.on('connection_response', (data) => {
      console.log('Connection response:', data);
    });

    // Listen for new uncertainties
    socketRef.current.on('uncertainty_created', (data) => {
      console.log('New uncertainty created:', data);
      setUncertainties(prev => {
        // Check if uncertainty already exists
        if (prev.some(u => u.id === data.uncertainty.id)) {
          return prev;
        }
        return [...prev, data.uncertainty];
      });
    });

    // Listen for new votes
    socketRef.current.on('vote_submitted', (data) => {
      console.log('New vote submitted:', data);
      setVotes(prev => {
        // Update or add vote
        const filtered = prev.filter(
          v => !(v.user_id === data.vote.user_id && v.uncertainty_id === data.vote.uncertainty_id)
        );
        return [...filtered, data.vote];
      });
    });

    // Listen for user presence
    socketRef.current.on('user_joined', (data) => {
      console.log('User joined:', data);
      setParticipants(prev => {
        if (prev.some(p => p.user_id === data.user_id)) return prev;
        return [...prev, { user_id: data.user_id, user_name: data.user_name, online: true }];
      });
    });

    socketRef.current.on('user_left', (data) => {
      console.log('User left:', data);
      setParticipants(prev => prev.filter(p => p.user_id !== data.user_id));
    });

    socketRef.current.on('session_participants', (data) => {
      console.log('Session participants:', data);
      setParticipants(data.participants);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [setUncertainties, setVotes]);

  // Join session room when activeSession changes
  useEffect(() => {
    if (socketRef.current && isConnected && currentUser && activeSession) {
      console.log('Joining session:', activeSession.id);
      socketRef.current.emit('join_session', {
        session_id: activeSession.id,
        user_id: currentUser.id,
        user_name: currentUser.name
      });

      return () => {
        socketRef.current.emit('leave_session', {
          session_id: activeSession.id,
          user_id: currentUser.id
        });
      };
    }
  }, [isConnected, currentUser, activeSession]);

  const value = {
    socket: socketRef.current,
    isConnected,
    participants
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};